import { TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createQuoteNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import classNames from 'classnames'
import { Bold, Italic, List, NumberedListLeft, QuoteMessage, Strikethrough, TaskList, Underline } from 'iconoir-react'
import {
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  KEY_MODIFIER_COMMAND,
  COMMAND_PRIORITY_NORMAL,
  $createParagraphNode,
  BaseSelection,
  LexicalNode,
} from 'lexical'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'app/components/Tooltip'

const toolbarButtonsClasses = `h-8 w-8 p-1 shrink-0 cursor-pointer flex justify-center align-middle items-middle text-default hover:bg-surface-strong`
const toolbarButtonActive = `bg-surface-strong bg-contain opacity-100`

function Divider() {
  return <div className="mx-1 w-[1px] bg-(--line-strong)" />
}

export default function EditorToolbar() {
  const [editor] = useLexicalComposerContext()
  const [activeEditor, setActiveEditor] = useState(editor)
  const toolbarRef = useRef(null)
  const [blockType, setBlockType] = useState(`paragraph`)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const isLink = false

  const formatParagraph = () => {
    if (blockType !== `paragraph`) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection as BaseSelection, () => $createParagraphNode())
        }
      })
    }
  }

  const formatBulletList = () => {
    if (blockType !== `ul`) {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }
  const formatCheckList = () => {
    if (blockType !== `check`) {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatNumberedList = () => {
    if (blockType !== `number`) {
      activeEditor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      activeEditor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatQuote = () => {
    if (blockType !== `quote`) {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection as BaseSelection, () => $createQuoteNode())
        }
      })
    } else formatParagraph()
  }

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      let element = anchorNode.getKey() === `root` ? anchorNode : anchorNode.getTopLevelElementOrThrow()
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode as LexicalNode, ListNode)
        const type = parentList ? parentList.getTag() : (element as ListNode).getTag()
        setBlockType(type)
      } else setBlockType(element.getType())

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow()
      }

      // Update text format
      setIsBold(selection.hasFormat(`bold`))
      setIsItalic(selection.hasFormat(`italic`))
      setIsUnderline(selection.hasFormat(`underline`))
      setIsStrikethrough(selection.hasFormat(`strikethrough`))
    }
  }, [activeEditor])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar()
        setActiveEditor(newEditor)
        return false
      },
      COMMAND_PRIORITY_CRITICAL,
    )
  }, [editor, $updateToolbar])

  useEffect(() => {
    return mergeRegister(
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      }),
    )
  }, [$updateToolbar, activeEditor, editor])

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      (payload) => {
        const event = payload as KeyboardEvent
        const { code, ctrlKey, metaKey } = event

        if (code === `KeyK` && (ctrlKey || metaKey)) {
          event.preventDefault()
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, `https://`)
        }
        return false
      },
      COMMAND_PRIORITY_NORMAL,
    )
  }, [activeEditor, isLink])

  return (
    <div className="flex rounded-t-md border bg-surface-subtle px-1.5 py-1 align-middle" ref={toolbarRef}>
      <Tooltip message="Font Bold" placement="top">
        <Bold
          strokeWidth={1.5}
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: isBold })}
          aria-label="Format Bold"
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, `bold`)}
        />
      </Tooltip>
      <Tooltip message="Font Italic" placement="top">
        <Italic
          strokeWidth={1.5}
          aria-label="Format Italic"
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: isItalic })}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, `italic`)}
        />
      </Tooltip>
      <Tooltip message="Underline" placement="top">
        <Underline
          strokeWidth={1.5}
          aria-label="Format Underline"
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: isUnderline })}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, `underline`)}
        />
      </Tooltip>
      <Tooltip message="Strikethrough" placement="top">
        <Strikethrough
          strokeWidth={1.5}
          aria-label="Format Strikethrough"
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: isStrikethrough })}
          onClick={() => activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, `strikethrough`)}
        />
      </Tooltip>
      <Tooltip message="Quote" placement="top">
        <QuoteMessage
          strokeWidth={1.5}
          aria-label="Format Quote"
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: blockType === `quote` })}
          onClick={formatQuote}
        />
      </Tooltip>
      <Divider />
      <Tooltip message="List" placement="top">
        <List
          strokeWidth={1.5}
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: blockType === `ul` })}
          onClick={formatBulletList}
        />
      </Tooltip>
      <Tooltip message="Numbered List" placement="top">
        <NumberedListLeft
          strokeWidth={1.5}
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: blockType === `ol` })}
          onClick={formatNumberedList}
        />
      </Tooltip>
      <Tooltip message="Check List" placement="top">
        <TaskList
          strokeWidth={1.5}
          className={classNames(toolbarButtonsClasses, { [toolbarButtonActive]: blockType === `check` })}
          onClick={formatCheckList}
        />
      </Tooltip>
    </div>
  )
}
